import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';
import { RouteReuseStrategy } from '@angular/router';
import Keycloak from 'keycloak-js';
import { environment as Config } from '@env/environment';
import { setToStorage } from '@shared/utility/session-local-storage';
import { StorageKeysEnum } from '@shared/utility/storage-keys';
import { updateMainFontSize } from '@shared/utility/utility';
import { applyUserTimezone, setSystemFallbackTimezone } from '@core/formatting';
import { applyUserFormatLocale } from '@core/language/format-locale';
import { ensureCrossTabStorageListener } from '@shared/utility/session-local-storage';
import { LoggerService } from '@core/log/services/logger';
import { injectEnsureCachedLoggedOperator } from '@core/operators/state/operator.cache';
import { CustomReuseStrategy } from '@core/router/custom-reuse-strategy';
import { injectEnsureCachedTagSystemGeneralInfo } from '@core/settings/state/tagsystem-info.cache';

export const initApp = async () => {
  const keycloak = inject(Keycloak);
  const logger = inject(LoggerService);
  const cachedLoggedOperator = injectEnsureCachedLoggedOperator();
  const cachedGeneralInfo = injectEnsureCachedTagSystemGeneralInfo();

  // Conservo la funzione originaria;
  const keycloakOnReadyFn = keycloak.onReady;
  const keycloakOnAuthErrorFn = keycloak.onAuthError;

  return new Promise<void>((resolve, reject) => {
    keycloak.onReady = (authenticated) => {
      // Invoco la funzione originaria se presente;
      keycloakOnReadyFn?.call(keycloak, authenticated);

      if (!authenticated) {
        setToStorage('sessionStorage', StorageKeysEnum.firstLogin, true);
        setToStorage(
          'sessionStorage',
          StorageKeysEnum.urlToOpenIfLogged,
          location.href.replace(`${location.origin}${Config.baseHref}`, '')
        );
        keycloak.login({ redirectUri: location.href });
      } else {
        Promise.allSettled([cachedLoggedOperator(), cachedGeneralInfo()]).then(
          ([operatorResult, generalInfoResult]) => {
            if (generalInfoResult.status === 'fulfilled') {
              const systemTz = generalInfoResult.value.find((i) => i.type === 'SYSTEM_TIME_ZONE')?.value;
              setSystemFallbackTimezone(systemTz);
            }

            if (operatorResult.status === 'fulfilled') {
              const o = operatorResult.value;
              if (o != null) {
                // Applica la dimensione dell'interfaccia scelta nelle preferenze utente
                const mainFontSize = o.operatorSettings?.otherSettings?.uiSize;
                if (mainFontSize != null) {
                  updateMainFontSize(mainFontSize);
                }
                // Imposta la timezone scelta dall'utente
                ensureCrossTabStorageListener();
                const userTimezone = o.operatorSettings?.timezone;
                if (userTimezone) {
                  applyUserTimezone(userTimezone);
                }
                // Lingua già in `curr_loc`: il Formato regionale la usa come fallback se non impostato.
                applyUserFormatLocale(o.operatorSettings?.formatLocale);
              }
              resolve();
            } else {
              const error = operatorResult.reason as unknown;
              const allowedErrors = new Set([HttpStatusCode.Forbidden, HttpStatusCode.UnavailableForLegalReasons]);
              if (error instanceof HttpErrorResponse && allowedErrors.has(error.status)) {
                // In questo caso può trattarsi di password reimpostata o di login non concesso via licenza/ruolo.
                resolve();
              }
              logger.error('Errore durante il login', { error });
              reject(error);
            }
          }
        );
      }
    };
    keycloak.onAuthError = (errorData) => {
      // Invoco la funzione originaria se presente;
      keycloakOnAuthErrorFn?.call(keycloak, errorData);

      logger.error("Errore durante l'autenticazione", { ...errorData });
      reject(errorData);
    };
  });
};

export const provideCustomReuseStrategy = (): EnvironmentProviders => {
  return makeEnvironmentProviders([{ provide: RouteReuseStrategy, useClass: CustomReuseStrategy }]);
};
